import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { z } from 'zod';
import { customFetcher } from './customFetcher';

const UnknownObjectSchema = z.record(z.unknown());
const WebAuthnOptionsSchema = z.object({}).passthrough();
const WebAuthnOptionsResponseSchema = z.union([
    WebAuthnOptionsSchema,
    z.object({ options: WebAuthnOptionsSchema }),
]);
const RegistrationOptionsRequestSchema = z.object({
    nickname: z.string().trim().min(1).optional(),
});
const AuthenticationOptionsRequestSchema = z.object({
    username: z.string().trim().min(1).optional(),
});
const RegistrationVerifyRequestSchema = z.object({
    credential: z.unknown(),
    nickname: z.string().trim().min(1).optional(),
});
const AuthenticationVerifyRequestSchema = z.object({
    credential: z.unknown(),
});

export const CredentialListItemSchema = z.object({
    credentialId: z.string(),
    nickname: z.string().nullable().optional(),
    createdAt: z.string(),
    lastUsedAt: z.string().nullable().optional(),
    transports: z.array(z.string()).nullable().optional(),
});

const CredentialListSchema = z.array(CredentialListItemSchema);

export const VerifyResponseSchema = z
    .object({
        ok: z.boolean(),
        token: z.string().optional(),
        access: z.string().optional(),
        refresh: z.string().optional(),
        groups: z.unknown().optional(),
        is_superadmin: z.union([z.string(), z.number(), z.boolean()]).optional(),
        user_id: z.union([z.string(), z.number()]).optional(),
        user: UnknownObjectSchema.optional(),
    })
    .passthrough();

export type CredentialListItem = z.infer<typeof CredentialListItemSchema>;
export type WebAuthnVerifyResponse = z.infer<typeof VerifyResponseSchema>;

type RegistrationOptionsJson = Parameters<typeof startRegistration>[0] extends {
    optionsJSON: infer T;
}
    ? T
    : never;

type AuthenticationOptionsJson = Parameters<typeof startAuthentication>[0] extends {
    optionsJSON: infer T;
}
    ? T
    : never;

const getOptionsFromResponse = (input: unknown) => {
    const parsed = WebAuthnOptionsResponseSchema.parse(input);
    if ('options' in parsed) {
        return parsed.options;
    }
    return parsed;
};

const postPublicJson = async (endpoint: string, body: unknown): Promise<unknown> => {
    const response = await fetch(`/api${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    if (response.status === 204) {
        return {};
    }

    return response.json() as Promise<unknown>;
};

export const useWebAuthnCredentials = (enabled: boolean = true) => {
    return useQuery<CredentialListItem[]>({
        enabled,
        refetchOnWindowFocus: false,
        queryKey: ['webauthn', 'credentials'],
        queryFn: async () => {
            const response = await customFetcher<unknown>({ endpoint: '/webauthn/credentials' });
            return CredentialListSchema.parse(response);
        },
    });
};

export const useRegisterWebAuthnCredential = () => {
    const queryClient = useQueryClient();

    return useMutation<WebAuthnVerifyResponse, Error, { nickname?: string }>({
        mutationFn: async ({ nickname }) => {
            const optionsRequestBody = RegistrationOptionsRequestSchema.parse(
                nickname ? { nickname } : {}
            );
            const optionsResponse = await customFetcher<unknown>({
                endpoint: '/webauthn/registration/options',
                method: 'POST',
                body: optionsRequestBody,
            });

            const options = getOptionsFromResponse(optionsResponse);
            const credential = await startRegistration({
                optionsJSON: options as RegistrationOptionsJson,
            });

            const verifyRequestBody = RegistrationVerifyRequestSchema.parse({
                credential,
                ...(nickname ? { nickname } : {}),
            });
            const verifyResponse = await customFetcher<unknown>({
                endpoint: '/webauthn/registration/verify',
                method: 'POST',
                body: verifyRequestBody,
            });

            return VerifyResponseSchema.parse(verifyResponse);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['webauthn', 'credentials'] });
        },
    });
};

export const useDeleteWebAuthnCredential = () => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, string>({
        mutationFn: async (credentialId) => {
            await customFetcher<unknown>({
                endpoint: `/webauthn/credentials/${encodeURIComponent(credentialId)}`,
                method: 'DELETE',
            });
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['webauthn', 'credentials'] });
        },
    });
};

export const useAuthenticateWithPasskey = () => {
    return useMutation<WebAuthnVerifyResponse, Error, { username?: string }>({
        mutationFn: async ({ username }) => {
            const optionsRequestBody = AuthenticationOptionsRequestSchema.parse(
                username ? { username } : {}
            );
            const optionsResponse = await postPublicJson(
                '/webauthn/authentication/options',
                optionsRequestBody
            );

            const options = getOptionsFromResponse(optionsResponse);
            const credential = await startAuthentication({
                optionsJSON: options as AuthenticationOptionsJson,
            });

            const verifyRequestBody = AuthenticationVerifyRequestSchema.parse({
                credential,
            });
            const verifyResponse = await postPublicJson('/webauthn/authentication/verify', verifyRequestBody);

            return VerifyResponseSchema.parse(verifyResponse);
        },
    });
};
